<template>
  <draggable
    ref="drag"
    v-model="files"
    v-bind="dragOptions"
    multi-drag
    tag="tbody"
    selected-class="multi-drag"
    @update="onUpdate"
    @add="onAdd"
    @remove="onRemove"
    @start="onDragStart"
    @end="onDragEnd"
  >
    <template v-if="files.length > 0">
      <tr v-for="item in files" :key="item.id">
        <td class="d-flex align-center">
          <div class="handle-drag" style="cursor: move">
            <v-icon>mdi-drag</v-icon>
          </div>
          <v-checkbox
            hide-details
            class="pa-0 ma-0"
            v-model="selectedDeliveries[item.id]"
            @change="onCheckboxChange"
          ></v-checkbox>
        </td>
        <td>
          {{ item?.deliveryNumber }}
        </td>
        <td>{{ formatPrice(item?.deliveryPrice) }}</td>
        <td>{{ formatPrice(item?.totalPriceNfes) }}</td>
        <td>{{ formatWeight(item?.weight) }}kg</td>
      </tr>
    </template>
    <template v-else>
      <tr>
        <td colspan="5" class="text-center">Nenhuma entrega alocado para essa parada</td>
      </tr>
    </template>
  </draggable>
</template>

<script>
import copyToClipboard from '@/utils/copyToClipboard';
import draggable from 'vuedraggable';
import maskPrice from '@/utils/maskPrice';
import maskWeight from '@/utils/maskWeight';
import { mapActions } from 'vuex';

export default {
  components: {
    draggable
  },
  props: {
    value: Array,
    selectAll: {
      type: Boolean,
      default: false
    },
    stop: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      selectedDeliveries: {},
      dragOptions: {
        animation: 150,
        group: 'deliveryAllocation',
        ghostClass: 'ghost',
        disabled: false,
        handle: '.handle-drag'
      },
      isDragging: false
    };
  },
  computed: {
    files: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      }
    }
  },
  watch: {
    selectAll() {
      this.toggleSelectAll();
    },
    files: {
      handler() {
        if (!this.files.length) {
          this.$emit('update:selectAll', false);
        }
      }
    },
    selectedDeliveries: {
      handler() {
        this.checkAllSelected();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('ImportManualStopsStore', ['allocateDelivery', 'deallocateDelivery']),
    async allocateDeliveryToStop(deliveries) {
      try {
        let deliveriesIds;
        if (Array.isArray(deliveries)) {
          deliveriesIds = deliveries.map(({ id }) => id);
        } else {
          deliveriesIds = [deliveries.id];
        }
        const params = {
          id: this.stop.id,
          deliveriesIds: deliveriesIds
        };

        const response = await this.allocateDelivery(params);
        if (response.success) {
          this.$emit('update:allocated', 'Entregas alocadas com sucesso!', true);
        } else {
          const draggedItemIndex = this.files.findIndex((item) => item === deliveries);
          if (draggedItemIndex !== -1) {
            this.files.splice(draggedItemIndex, 1);
          }
          this.$emit(
            'update:allocated',
            response.errors[0] || 'Entregas com destinatário ou remetente distintos!',
            false
          );
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deallocateDeliveryFromStop(deliveries) {
      try {
        let deliveriesIds;
        if (Array.isArray(deliveries)) {
          deliveriesIds = deliveries.map(({ id }) => id);
        } else if (deliveries && deliveries.id) {
          deliveriesIds = [deliveries.id];
        } else {
          console.error('Invalid deliveries object:', deliveries);
          return;
        }
        const params = {
          id: this.stop.id,
          deliveriesIds: deliveriesIds
        };
        const response = await this.deallocateDelivery(params);
        if (response.success) {
          this.$emit('update:allocated', 'Entregas desalocadas com sucesso!', true);
        } else {
          this.$emit('update:allocated', response.errors[0] || 'Erro ao desalocar Entregas', false);
        }
      } catch (err) {
        console.error(err);
      }
    },
    toggleSelectAll() {
      this.selectedDeliveries = {};
      if (this.selectAll) {
        this.files.forEach((delivery) => {
          this.$set(this.selectedDeliveries, delivery.id, true);
        });
      } else {
        this.selectedDeliveries = {};
      }
    },
    onDragStart(e) {
      if (this.files.length > 1) {
        e.item._selected_items = this.files.filter((f) => this.selectedDeliveries[f.id]);
      } else {
        e.item._selected_items = [this.files[0]];
      }
    },
    onDragEnd() {
      this.isDragging = false;
      this.selectedDeliveries = {};
    },
    onUpdate(e) {
      if (e.item._selected_items && e.item._selected_items.length) {
        const ids = e.item._selected_items.map(({ id }) => id);
        const files = this.files.filter((f) => !ids.includes(f.id));
        files.splice(e.newIndex, 0, ...e.item._selected_items);
        this.files = files;
      }
    },
    onRemove(e) {
      let item;
      if (e.item._selected_items && e.item._selected_items.length > 1) {
        item = e.item._selected_items;
      } else if (e.item._underlying_vm_) {
        item = [e.item._underlying_vm_];
      } else {
        console.error('Invalid item on remove:', e.item);
        return;
      }
      this.deallocateDeliveryFromStop(item);
      this.$emit('update:selectAll', false);
    },
    async onAdd(e) {
      this.$emit('loading:allocated', true);
      if (e.item._selected_items && e.item._selected_items.length > 1) {
        const draggedItems = e.item._selected_items;
        await this.allocateDeliveryToStop(draggedItems);
      } else {
        await this.allocateDeliveryToStop(e.item._underlying_vm_);
      }
      this.$emit('loading:allocated', false);
      this.$emit('update:selectAll', false);
    },
    toggleSelect(id) {
      this.$set(this.selectedDeliveries, id, !this.selectedDeliveries[id]);
      this.checkAllSelected();
    },
    onCheckboxChange() {
      this.checkAllSelected();
    },
    checkAllSelected() {
      const allSelected = this.files.length > 0 && this.files.every((delivery) => this.selectedDeliveries[delivery.id]);
      this.$emit('update:selectAll', allSelected);
    },
    formatPrice(price) {
      return maskPrice(price);
    },
    formatWeight(price) {
      return maskWeight(price);
    },
    copy(str) {
      copyToClipboard(str);
    }
  }
};
</script>

<style scoped>
.ghost {
  display: none;
}
</style>
